import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useSubscription } from "@/context/SubscriptionContext";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import {
  Check, Sparkles, Loader2, Crown, Gift, Copy,
  Layers, LineChart, Bot, Compass, Star, History, FileDown, GitCompareArrows, ShieldCheck,
  Building2, Mail, Wallet, Bell, Newspaper, Download, Wand2, Target, Smartphone, MessageSquare,
} from "lucide-react";
import usePageSeo from "@/hooks/usePageSeo";
import { isNative } from "@/lib/native";
import { NativeIapButton, NativeIapFooter } from "@/components/NativeIap";
import PlanPickerQuiz from "@/components/pricing/PlanPickerQuiz";
import ContactDialog from "@/components/ContactDialog";
import DownloadPill from "@/components/DownloadPill";
import PricingScorecard from "@/components/pricing/PricingScorecard";

const TID = {
  beginnerSubscribe: "pricing-beginner-subscribe",
  proSubscribe: "pricing-pro-subscribe",
  freeCta: "pricing-free-cta",
  currentPlan: "pricing-current-plan",
  manageBilling: "pricing-manage-billing",
  billingMonthly: "pricing-billing-monthly",
  billingYearly: "pricing-billing-yearly",
  referralCode: "referral-code",
  referralCopy: "referral-copy",
  referralCount: "referral-count",
  featuresList: "pricing-features-list",
  featureChart: "pricing-feature-chart",
  teamContact: "pricing-team-contact",
  teamSeats: "pricing-team-seats",
  contactOpen: "pricing-contact-open",
  contactDialog: "pricing-contact-dialog",
  contactName: "pricing-contact-name",
  contactEmail: "pricing-contact-email",
  contactMessage: "pricing-contact-message",
  contactSubmit: "pricing-contact-submit",
  contactCancel: "pricing-contact-cancel",
};

// Team-tier config — per-seat monthly billing. Billing happens via the existing
// Admin Control Room bulk-invite flow (Pre-approval + Grant Core) after a sales
// chat, so we don't need a new Stripe product yet. The contact form uses the
// waitlist API to capture interest server-side.
const TEAM = {
  perSeatMonthly: 7.99,
  minSeats: 5,
  perksSummary: [
    "Everything in Core, per seat",
    "Bulk invite via CSV (Admin Control Room)",
    "Centralized billing & seat management",
    "Priority support & onboarding call",
  ],
};

// Two paid tiers now. Core ($9.99/mo, 10 portfolios) is the new
// on-ramp for casual users; Pro ($14.99/mo, unlimited) is the power-user
// tier. Both share yearly discounts through the same Stripe pattern.
const PLANS = {
  beginner: {
    monthly: {
      lookupKey: "beginner_monthly",
      amount: 9.99,
      period: "/ month",
      savings: null,
      perMonthEquivalent: null,
      trial: null,
    },
    yearly: {
      lookupKey: "beginner_yearly",
      amount: 95.99,
      period: "/ year",
      savings: "Save 20%",
      perMonthEquivalent: "≈ $8.00/mo · billed yearly",
      trial: "Start with a 3-day free trial",
    },
  },
  pro: {
    monthly: {
      lookupKey: "pro_monthly",
      amount: 14.99,
      period: "/ month",
      savings: null,
      perMonthEquivalent: null,
      trial: null,
    },
    yearly: {
      lookupKey: "pro_yearly",
      amount: 143.99,
      period: "/ year",
      savings: "Save 20%",
      perMonthEquivalent: "≈ $12.00/mo · billed yearly",
      trial: "Start with a 3-day free trial",
    },
  },
};

// Every feature we ship, grouped and stamped with what each plan gets.
// Values across `free` / `beginner` / `pro`: true = included, false = not included,
// or a short label like "1", "Unlimited". Order: highest-value groups first so
// the reader sees the killer features before scrolling.
const FEATURE_GROUPS = [
  {
    icon: Wallet,
    title: "Track My Holdings",
    items: [
      { name: "Multiple named accounts (Roth · Taxable · 401k · IRA · HSA)", free: "Up to 10", beginner: "Up to 10", pro: "Up to 10" },
      { name: "Multi-portfolio grouping (retirement · taxable · custom)", free: true, beginner: true, pro: true, badge: "New" },
      { name: "Live portfolio value, unrealized gain, yield-on-cost", free: true, beginner: true, pro: true },
      { name: "Average cost per share on every position", free: true, beginner: true, pro: true, badge: "New" },
      { name: "Portfolio value growth chart (30D · 90D · 1Y)", free: true, beginner: true, pro: true, badge: "New" },
      { name: "Monthly dividend income chart (12-24 month seasonality)", free: true, beginner: true, pro: true, badge: "New" },
      { name: "Ex-dividend calendar with month grid (past + upcoming)", free: true, beginner: true, pro: true, badge: "New" },
      { name: "Same-ticker rollup across every account", free: true, beginner: true, pro: true },
      { name: "One-click CSV export (14 columns + totals row)", free: true, beginner: true, pro: true, badge: "New" },
      { name: "Paste positions from any broker (TICKER,SHARES,COST)", free: true, beginner: true, pro: true },
      { name: "Broker Sync via SnapTrade (read-only, production)", free: false, beginner: true, pro: true },
      { name: "Access token encrypted at rest (Fernet)", free: false, beginner: true, pro: true },
      { name: "Forward monthly & annual dividend income", free: true, beginner: true, pro: true },
      { name: "Per-position % of portfolio & % of income", free: true, beginner: true, pro: true },
      { name: "Dashboard rollup card across all accounts", free: true, beginner: true, pro: true },
      { name: "Total Holdings card with 30-day sparkline & shareable image", free: true, beginner: true, pro: true, badge: "New" },
      { name: "Personal income goal tracker (forward income vs goal)", free: true, beginner: true, pro: true, badge: "New" },
    ],
  },
  {
    icon: Wand2,
    title: "Holdings Optimizer",
    items: [
      { name: "AI reweighting of the positions you already own", free: false, beginner: false, pro: true, badge: "Pro" },
      { name: "Your limit on how far each position can move (± pts)", free: false, beginner: false, pro: true },
      { name: "Before/after income, yield, CAGR & total return backtest", free: false, beginner: false, pro: true },
      { name: "Buy/sell trade list in dollars & whole shares", free: false, beginner: false, pro: true },
      { name: "Dividend payout frequency per holding", free: false, beginner: false, pro: true },
      { name: "Save the proposal as a new portfolio", free: false, beginner: false, pro: true },
    ],
  },
  {
    icon: Bot,
    title: "AI intelligence",
    items: [
      { name: "AI explanations", free: "4 / month", beginner: "Up to 10 / mo", pro: "Unlimited" },
      { name: "AI Optimizer — rebalance for yield / CAGR / balanced", free: "Counts toward 4 / mo", beginner: "Up to 10 / mo", pro: "Unlimited" },
      { name: "AI Income Target Planner — reverse-solve for a goal", free: "Counts toward 4 / mo", beginner: "Up to 10 / mo", pro: "Unlimited" },
      { name: "AI thesis for any ticker", free: "Counts toward 4 / mo", beginner: "Up to 10 / mo", pro: "Unlimited" },
      { name: "Streaming, follow-ups, copy-to-clipboard", free: true, beginner: true, pro: true },
      { name: "AI explanation chats saved with each portfolio", free: false, beginner: false, pro: true, badge: "Pro" },
    ],
  },
  {
    icon: MessageSquare,
    title: "Ask DPP assistant",
    items: [
      { name: "AI analyst that runs the numbers on your own data", free: true, beginner: true, pro: true, badge: "New" },
      { name: "Multi-step plans with one-tap action buttons", free: true, beginner: true, pro: true },
      { name: "Conversation history · resume any thread", free: true, beginner: true, pro: true },
      { name: "Pin favourites · export chats to Markdown or PDF", free: true, beginner: true, pro: true },
    ],
  },
  {
    icon: LineChart,
    title: "Backtesting engine",
    items: [
      { name: "30+ years of price & dividend history", free: true, beginner: true, pro: true },
      { name: "Cached backtest results", free: true, beginner: true, pro: true },
      { name: "Priority backtest queue", free: false, beginner: false, pro: true },
      { name: "Multi-scenario compare view", free: true, beginner: true, pro: true },
    ],
  },
  {
    icon: Layers,
    title: "Portfolio building",
    items: [
      { name: "Saved portfolios", free: "1", beginner: "Up to 10", pro: "Unlimited" },
      { name: "Weight sliders & auto-balance", free: true, beginner: true, pro: true },
      { name: "Ticker autocomplete (curated + yfinance)", free: true, beginner: true, pro: true },
      { name: "DRIP toggle & mid-range dates", free: true, beginner: true, pro: true },
      { name: "Monthly / quarterly / yearly contributions (DCA)", free: true, beginner: true, pro: true },
      { name: "Instant Income Planner amounts · apply to portfolio in one tap", free: true, beginner: true, pro: true, badge: "New" },
      { name: "Backtest, optimizer & planner results saved with the portfolio", free: true, beginner: true, pro: true, badge: "New" },
    ],
  },
  {
    icon: Compass,
    title: "Ticker research",
    items: [
      { name: "Company snapshot, price chart (8 ranges)", free: true, beginner: true, pro: true },
      { name: "Performance & risk (5y CAGR, vol, Sharpe, drawdown)", free: true, beginner: true, pro: true },
      { name: "Dividend safety score & payout history", free: true, beginner: true, pro: true },
      { name: "Ex-dividend calendar & news", free: true, beginner: true, pro: true },
      { name: "Theme browsing (Aristocrats, REITs, etc.)", free: true, beginner: true, pro: true },
      { name: "Side-by-side ticker compare", free: true, beginner: true, pro: true },
    ],
  },
  {
    icon: Bell,
    title: "Notifications & delivery",
    items: [
      { name: "Morning ex-dividend push alerts (phone & browser)", free: true, beginner: true, pro: true },
      { name: "Configurable lead time (day-of · 1-day · 2-day)", free: true, beginner: true, pro: true, badge: "New" },
      { name: "The DPP Weekly — personalized Saturday digest", free: true, beginner: true, pro: true },
      { name: "Native iPhone & Android apps · install on desktop", free: true, beginner: true, pro: true, badge: "New" },
      { name: "Sign in with Apple or Google", free: true, beginner: true, pro: true },
      { name: "Weekly Market Recap", free: true, beginner: true, pro: true },
      { name: "Contact support in-app + email replies", free: true, beginner: true, pro: true },
    ],
  },
  {
    icon: Star,
    title: "Watchlist & tracking",
    items: [
      { name: "Star tickers on Research page", free: true, beginner: true, pro: true },
      { name: "Dashboard watchlist with live yield & price", free: true, beginner: true, pro: true },
      { name: "One-click Add to new portfolio from watchlist", free: true, beginner: true, pro: true },
    ],
  },
  {
    icon: History,
    title: "Optimizer history",
    items: [
      { name: "Versioned portfolio snapshots", free: true, beginner: true, pro: true },
      { name: "Auto-save baseline before AI apply", free: true, beginner: true, pro: true },
      { name: "Pin & compare any two snapshots", free: true, beginner: true, pro: true },
      { name: "One-click restore any version", free: true, beginner: true, pro: true },
    ],
  },
  {
    icon: FileDown,
    title: "Reports & export",
    items: [
      { name: "CSV export (portfolios, backtests, holdings, users)", free: true, beginner: true, pro: true },
      { name: "CSV import with diff-preview modal", free: true, beginner: true, pro: true },
      { name: "Branded PDF backtest report with cover page", free: false, beginner: false, pro: true, badge: "Pro" },
    ],
  },
  {
    icon: Gift,
    title: "Community & rewards",
    items: [
      { name: "Referral credits (+30 days of Pro per friend)", free: true, beginner: true, pro: true },
      { name: "Vote on public roadmap", free: true, beginner: true, pro: true },
      { name: "Cancel anytime (Stripe on web · App Store / Google Play in the app)", free: true, beginner: true, pro: true },
    ],
  },
];

// Quick-scan feature chart shown ABOVE the detailed comparison. Just the
// killer differentiators between tiers so casual visitors can pick a tier
// without wading through the whole table.
const FEATURE_CHART = [
  { name: "Saved portfolios",                          free: "1",         beginner: "Up to 10",      pro: "Unlimited" },
  { name: "AI actions (Optimizer · Planner · thesis)",  free: "4 / month", beginner: "10 / month",    pro: "Unlimited" },
  { name: "Ask DPP AI assistant + chat history",       free: true,        beginner: true,            pro: true },
  { name: "30-year backtest engine with DCA & DRIP",   free: true,        beginner: true,            pro: true },
  { name: "Track holdings + income goal progress",     free: true,        beginner: true,            pro: true },
  { name: "Brokerage sync (read-only)",                free: false,       beginner: true,            pro: true },
  { name: "Holdings Optimizer + trade list",           free: false,       beginner: false,           pro: true },
  { name: "Saved AI chats · branded PDF reports",      free: false,       beginner: false,           pro: true },
  { name: "Priority backtest queue",                   free: false,       beginner: false,           pro: true },
  { name: "Ex-dividend alerts · iPhone & Android apps", free: true,       beginner: true,            pro: true },
];

// Marker component used inline in feature rows. Accent choice determines the
// tick color / chip background — one per tier column so the eye can differentiate.
function Marker({ v, accent }) {
  const tickCls =
    accent === "team" ? "text-primary" :
    accent === "pro" ? "text-secondary" :
    accent === "beginner" ? "text-emerald-600" :
    "text-primary";
  const chipCls =
    accent === "team" ? "bg-primary/10 text-primary" :
    accent === "pro" ? "bg-secondary/15 text-secondary" :
    accent === "beginner" ? "bg-emerald-500/15 text-emerald-700 dark:text-emerald-400" :
    "bg-primary/10 text-primary";
  if (v === true) return <Check className={`w-4 h-4 ${tickCls}`} />;
  if (v === false) return <span className="mono text-[10px] uppercase tracking-widest text-muted-foreground/60">—</span>;
  return (
    <span className={`mono text-[10px] uppercase tracking-widest px-1.5 py-0.5 rounded ${chipCls}`}>
      {v}
    </span>
  );
}

export default function Pricing() {
  const nav = useNavigate();
  const { user } = useAuth();
  const { sub, refresh } = useSubscription();
  const [loading, setLoading] = useState(false);
  const [portalLoading, setPortalLoading] = useState(false);
  const [billing, setBilling] = useState("monthly");
  const [referral, setReferral] = useState(null);
  const [contactOpen, setContactOpen] = useState(false);
  // Live aggregate rating. Google's structured-data policy requires
  // aggregateRating to reflect real user reviews, so we only inject the field
  // once the backend reports `show_in_schema: true` (≥50 reviews).
  const [ratingAgg, setRatingAgg] = useState(null);
  useEffect(() => {
    api.get("/reviews/aggregate")
      .then((r) => setRatingAgg(r.data))
      .catch(() => setRatingAgg({ show_in_schema: false }));
  }, []);

  const isPro = sub?.active;
  // billing = "monthly" | "yearly" — toggled by the segmented control at the
  // top of the 4-card grid, applied to both Core and Pro cards.
  const beginnerPlan = PLANS.beginner[billing];
  const proPlan      = PLANS.pro[billing];

  usePageSeo({
    title: "Pricing — Dividend Portfolio Planner · Free · Core $9.99 · Pro $14.99",
    description:
      "Free (1 portfolio, 4 AI/mo). Core $9.99/mo (10 portfolios, 10 AI). Pro $14.99/mo unlimited. 30-year backtests on every plan.",
    path: "/pricing",
    ogType: "product",
    ogSlug: "pricing",
    jsonLdId: "pricing-jsonld",
    jsonLd: {
      "@context": "https://schema.org",
      "@graph": [
        // Product + AggregateOffer — surfaces rich price snippets in Google.
        {
          "@type": "Product",
          "@id": "https://dividendportfolioplanner.com/pricing#product",
          name: "Dividend Portfolio Planner",
          description:
            "Free (1 portfolio, 4 AI/mo). Core $9.99/mo (10 portfolios, 10 AI). Pro $14.99/mo unlimited. 30-year backtests on every plan.",
          brand: { "@type": "Brand", name: "Dividend Portfolio Planner" },
          image: "https://dividendportfolioplanner.com/logo.png",
          // aggregateRating is added below IFF the live backend aggregate
          // clears the MIN_REVIEWS threshold — otherwise Google penalizes
          // us for fabricated review markup.
          ...(ratingAgg?.show_in_schema ? {
            aggregateRating: {
              "@type": "AggregateRating",
              ratingValue: String(ratingAgg.average),
              reviewCount: String(ratingAgg.count),
            },
          } : {}),
          offers: {
            "@type": "AggregateOffer",
            priceCurrency: "USD",
            lowPrice: "0",
            highPrice: "143.99",
            offerCount: 6,
            availability: "https://schema.org/InStock",
            offers: [
              { "@type": "Offer", price: "0",      priceCurrency: "USD", name: "Free",                      url: "https://dividendportfolioplanner.com/pricing" },
              { "@type": "Offer", price: "9.99",   priceCurrency: "USD", name: "Core monthly",          url: "https://dividendportfolioplanner.com/pricing", priceValidUntil: "2027-12-31" },
              { "@type": "Offer", price: "95.99",  priceCurrency: "USD", name: "Core yearly (20% off)", url: "https://dividendportfolioplanner.com/pricing", priceValidUntil: "2027-12-31" },
              { "@type": "Offer", price: "14.99",  priceCurrency: "USD", name: "Pro monthly",               url: "https://dividendportfolioplanner.com/pricing", priceValidUntil: "2027-12-31" },
              { "@type": "Offer", price: "143.99", priceCurrency: "USD", name: "Pro yearly (20% off)",      url: "https://dividendportfolioplanner.com/pricing", priceValidUntil: "2027-12-31" },
              { "@type": "Offer", price: "7.99",   priceCurrency: "USD", name: "Team (per seat, monthly)",  url: "https://dividendportfolioplanner.com/pricing" },
            ],
          },
        },
        // SoftwareApplication — Bing/Google app-listing rich card.
        {
          "@type": "SoftwareApplication",
          "@id": "https://dividendportfolioplanner.com/#software",
          name: "Dividend Portfolio Planner",
          applicationCategory: "FinanceApplication",
          operatingSystem: "Web · iOS · Android · PWA",
          offers: { "@type": "Offer", price: "14.99", priceCurrency: "USD" },
          ...(ratingAgg?.show_in_schema ? {
            aggregateRating: { "@type": "AggregateRating", ratingValue: String(ratingAgg.average), reviewCount: String(ratingAgg.count) },
          } : {}),
        },
        // FAQPage — answers Google People-Also-Ask style intents.
        {
          "@type": "FAQPage",
          "@id": "https://dividendportfolioplanner.com/pricing#faq",
          mainEntity: [
            {
              "@type": "Question",
              name: "How much does the Dividend Portfolio Planner cost?",
              acceptedAnswer: { "@type": "Answer", text: "DPP is free forever for one saved portfolio, 4 AI actions per month, plus 30+ year backtests, live holdings tracking, ex-dividend push alerts, avg cost tracking, and CSV export. Core is $9.99/mo (or $95.99/yr, 20% off) and unlocks 10 saved portfolios, 10 AI actions per month (Optimizer, Income Target Planner, ticker thesis), and SnapTrade broker sync. Pro is $14.99/mo (or $143.99/yr) and adds unlimited saved portfolios, unlimited AI, and a priority backtest queue. Team seats start at $7.99/seat/month with a 5-seat minimum." },
            },
            {
              "@type": "Question",
              name: "How does DPP compare to Snowball, Sharesight, and Seeking Alpha?",
              acceptedAnswer: { "@type": "Answer", text: "DPP is the only tool that combines a 30-year backtest engine, AI planners (Optimizer + Income Target), live multi-account holdings rollup, SnapTrade broker sync (Schwab, Fidelity, Vanguard, Robinhood, IBKR, and 40+ others), ex-dividend push alerts, avg cost tracking, monthly-income seasonality charts, and multi-portfolio grouping — in a single app, at a lower paid price ($9.99/mo Core or $14.99/mo Pro vs $14 Snowball, $15 Sharesight, $19.92 Seeking Alpha)." },
            },
            {
              "@type": "Question",
              name: "Is the free plan actually free forever?",
              acceptedAnswer: { "@type": "Answer", text: "Yes. No credit card, no trial expiry, no ads. Free includes 1 saved portfolio, the full backtest engine, 4 AI actions per month, Track My Holdings (accounts, ex-div calendar, growth chart, income chart, CSV export), the watchlist, and ticker research. SnapTrade broker sync unlocks on Core and above." },
            },
            {
              "@type": "Question",
              name: "What is the difference between Core and Pro?",
              acceptedAnswer: { "@type": "Answer", text: "Core ($9.99/mo) unlocks up to 10 saved portfolios and up to 10 AI actions per month. Pro ($14.99/mo) removes both caps entirely — unlimited saved scenarios AND unlimited AI — and adds a priority backtest queue so results return faster on peak-load days." },
            },
            {
              "@type": "Question",
              name: "Can I cancel any time?",
              acceptedAnswer: { "@type": "Answer", text: "Yes. Stripe handles billing and cancellations. Paid tiers downgrade to Free at the end of the billing period — your data stays intact. Yearly plans come with a 3-day free trial that you can cancel with a single click." },
            },
            {
              "@type": "Question",
              name: "Does DPP connect to my brokerage?",
              acceptedAnswer: { "@type": "Answer", text: "Yes — DPP uses SnapTrade (production) for read-only broker sync (Schwab, Fidelity, Vanguard, Robinhood, IBKR, and 40+ others). Your SnapTrade user secret is encrypted at rest with Fernet. Positions, cost basis, and cash balances sync in real time; transaction-level dividend history is available on-demand via the Sync history button." },
            },
          ],
        },
        // BreadcrumbList — Google shows the trail under the SERP link.
        {
          "@type": "BreadcrumbList",
          "@id": "https://dividendportfolioplanner.com/pricing#breadcrumbs",
          itemListElement: [
            { "@type": "ListItem", position: 1, name: "Home",    item: "https://dividendportfolioplanner.com/" },
            { "@type": "ListItem", position: 2, name: "Pricing", item: "https://dividendportfolioplanner.com/pricing" },
          ],
        },
      ],
    },
  });

  const loadReferral = useCallback(async () => {
    if (!user) { setReferral(null); return; }
    try {
      const res = await api.get("/referral/me");
      setReferral(res.data);
    } catch (err) {
      console.debug("referral fetch failed (non-critical):", err?.message || err);
    }
  }, [user]);

  useEffect(() => { loadReferral(); }, [loadReferral]);

  // Deep link from the landing-page quiz: /pricing?plan=core|pro|free highlights that card.
  useEffect(() => {
    const plan = new URLSearchParams(window.location.search).get("plan");
    if (!["free", "core", "pro"].includes(plan)) return;
    const t = setTimeout(() => highlightPlan(plan), 700);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const shareUrl = referral ? `${window.location.origin}/auth?ref=${referral.code}` : "";

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      toast.success("Referral link copied");
    } catch {
      toast.error("Could not copy — select the text and copy manually");
    }
  };

  const startCheckout = async (lookupKey) => {
    if (!user) { nav("/auth"); return; }
    setLoading(true);
    try {
      const res = await api.post("/payments/checkout", {
        lookup_key: lookupKey,
        origin_url: window.location.origin,
      });
      window.location.href = res.data.checkout_url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not start checkout");
      setLoading(false);
    }
  };

  const openBillingPortal = async () => {
    setPortalLoading(true);
    try {
      const res = await api.post("/payments/billing-portal", {
        return_url: window.location.origin + "/pricing",
      });
      window.location.href = res.data.url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not open billing portal");
      setPortalLoading(false);
    }
  };

  // 7-day, no-card Core trial. Anonymous users are routed through auth
  // first; authenticated users hit the backend directly and land back on the
  // pricing page with a fresh subscription state.
  const [trialLoading, setTrialLoading] = useState(false);
  const startTrial = async () => {
    if (!user) { nav("/auth?intent=start-trial"); return; }
    setTrialLoading(true);
    try {
      await api.post("/subscription/start-trial");
      toast.success("7-day Core trial activated — enjoy!");
      await refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not start trial");
    } finally {
      setTrialLoading(false);
    }
  };
  const trialUsed = !!sub?.trial_used;
  const [quizOpen, setQuizOpen] = useState(false);
  const highlightPlan = (plan) => {
    const el = document.querySelector(`[data-testid="pricing-card-${plan === "core" ? "beginner" : plan}"]`);
    if (!el) return;
    el.scrollIntoView({ behavior: "smooth", block: "center" });
    const cls = ["outline", "outline-4", "outline-secondary/60", "outline-offset-4"];
    el.classList.add(...cls);
    setTimeout(() => el.classList.remove(...cls), 4000);
  };

  return (
    <div className="max-w-7xl mx-auto px-6 py-16">
      {/* Header */}
      <div className="text-center max-w-2xl mx-auto">
        <div className="mono text-[11px] uppercase tracking-[0.25em] text-secondary">Pricing</div>
        <h1 className="serif text-5xl sm:text-6xl mt-3">Simple. Fair. Yearly-friendly.</h1>
        <p className="text-muted-foreground mt-4">
          Start free with the full backtest engine and Ask DPP. Upgrade when you're ready to sync your brokerage, plan your income and optimize what you own. Cancel anytime.
        </p>
        {sub?.plan && sub.plan !== "free" && (
          <div data-testid={TID.currentPlan} className="inline-flex items-center gap-2 mt-6 px-3 py-1.5 rounded-full bg-primary/10 text-primary border border-primary/20 mono text-xs uppercase tracking-widest">
            <Crown className="w-3.5 h-3.5" />
            {sub.pro_trial_ends_at
              ? <>Pro trial · ends {new Date(sub.pro_trial_ends_at).toLocaleDateString()}</>
              : <>You&apos;re on {sub.plan === "pro" ? "Pro" : "Core"}</>}
          </div>
        )}
        <div className="mt-6 flex justify-center gap-3 flex-wrap">
          <Button
            variant="outline"
            onClick={() => document.getElementById("pricing-scorecard")?.scrollIntoView({ behavior: "smooth", block: "start" })}
            className="rounded-full"
            data-testid="pricing-compare-btn"
          >
            <GitCompareArrows className="w-4 h-4 mr-2 shrink-0" /><span className="sm:hidden">Compare vs other apps</span><span className="hidden sm:inline">Compare vs Snowball · Sharesight · Seeking Alpha</span>
          </Button>
          <Button
            onClick={() => setQuizOpen(true)}
            className="rounded-full bg-secondary text-secondary-foreground hover:bg-secondary/90"
            data-testid="pricing-quiz-open"
          >
            <Compass className="w-4 h-4 mr-2" /> Help me choose
          </Button>
          <Button
            variant="outline"
            onClick={() => setContactOpen(true)}
            className="rounded-full"
            data-testid={TID.contactOpen}
          >
            <Mail className="w-4 h-4 mr-2" /> Contact us
          </Button>
          <DownloadPill testId="pricing-download" label="Download the app" />
        </div>
      </div>

      {/* Billing toggle — one control drives both Core and Pro cards */}
      {!isPro && (
        <div className="mt-10 flex justify-center">
          <div className="inline-flex items-center rounded-full border border-border bg-muted/50 p-0.5">
            <button
              data-testid={TID.billingMonthly}
              onClick={() => setBilling("monthly")}
              className={`mono text-[11px] uppercase tracking-widest px-4 py-1.5 rounded-full transition-colors ${
                billing === "monthly" ? "bg-card text-foreground shadow-sm" : "text-muted-foreground hover:text-foreground"
              }`}
            >Monthly</button>
            <button
              data-testid={TID.billingYearly}
              onClick={() => setBilling("yearly")}
              className={`mono text-[11px] uppercase tracking-widest px-4 py-1.5 rounded-full transition-colors ${
                billing === "yearly" ? "bg-card text-foreground shadow-sm" : "text-muted-foreground hover:text-foreground"
              }`}
            >Yearly · <span className="text-secondary">-20%</span></button>
          </div>
        </div>
      )}

      {/* ------------------------------------------------------------
          THREE PUBLIC PRICING CARDS (Free · Core · Pro)
          Team pricing moved to Admin Control Room per user request
          (2026-02-13) so the public grid stays focused on retail.
      ------------------------------------------------------------ */}
      <div className="mt-8 grid gap-4 sm:grid-cols-2 lg:grid-cols-3 items-stretch" data-testid="pricing-cards">
        {/* Card 1 — Free */}
        <Card className={`h-full border ${!isPro ? "border-primary/30" : "border-border"} card-hover`} data-testid="pricing-card-free">
          <CardContent className="p-6 flex flex-col h-full">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <ShieldCheck className="w-5 h-5 text-primary" />
                <h2 className="serif text-2xl">Free</h2>
              </div>
              {!isPro && (
                <span className="mono text-[10px] uppercase tracking-widest text-primary">Current</span>
              )}
            </div>
            <p className="text-xs text-muted-foreground mt-1.5">See what dividend investing can do for you. No card needed.</p>
            <div className="mt-4 flex items-baseline gap-1.5">
              <span className="serif text-4xl">$0</span>
              <span className="mono text-xs text-muted-foreground">/ forever</span>
            </div>
            <div className="mono text-[10px] uppercase tracking-widest text-muted-foreground mt-1">1 saved portfolio</div>
            <ul className="mt-4 space-y-1.5 text-sm flex-1">
              <PerkRow><b>Backtest any dividend portfolio</b> over 30 years, with DRIP &amp; monthly contributions</PerkRow>
              <PerkRow><b>Ask DPP</b> — an AI analyst that runs the numbers for you</PerkRow>
              <PerkRow>Track holdings manually &amp; follow an income goal</PerkRow>
              <PerkRow>4 AI actions / month · ex-dividend alerts · CSV export</PerkRow>
            </ul>
            <Button
              data-testid={TID.freeCta}
              variant="outline"
              className="w-full mt-5 rounded-full"
              onClick={() => nav(user ? "/dashboard" : "/auth")}
            >
              {user ? "Open dashboard" : "Get started free"}
            </Button>
          </CardContent>
        </Card>

        {/* Card 2 — Core */}
        <PricingTierCard
          testId="pricing-card-beginner"
          icon={Sparkles}
          iconClass="text-emerald-600"
          borderClass="border-emerald-500/40"
          name="Core"
          tagline="Sync your brokerage and plan the income you want."
          plan={beginnerPlan}
          billing={billing}
          badge={{ label: "Most popular", cls: "bg-emerald-500/15 text-emerald-700 dark:text-emerald-400" }}
          portfolioLabel="Up to 10 saved portfolios"
          perks={[
            <><b>Sync your brokerage</b> (read-only) — live value, forward income &amp; dividend calendar</>,
            <><b>10 AI actions / month</b> — Income Planner, portfolio Optimizer &amp; ticker thesis</>,
            <><b>Compare up to 10 portfolios</b> side by side, results saved with each one</>,
            "Ex-dividend phone alerts · iPhone & Android apps",
          ]}
          buttonAccent="secondary"
          buttonTestId={TID.beginnerSubscribe}
          onSubscribe={() => startCheckout(beginnerPlan.lookupKey)}
          isCurrent={isPro && sub?.lookup_key?.startsWith("beginner")}
          isPro={isPro}
          nativeTier="core"
          subSource={sub?.source}
          onNativePurchased={refresh}
          loading={loading}
          openBillingPortal={openBillingPortal}
          portalLoading={portalLoading}
          trialCta={{
            show: !isPro && !trialUsed,
            loading: trialLoading,
            onClick: startTrial,
            testId: "pricing-beginner-trial",
          }}
        />

        {/* Card 3 — Pro */}
        <PricingTierCard
          testId="pricing-card-pro"
          icon={Crown}
          iconClass="text-secondary"
          borderClass="border-2 border-secondary/60"
          name="Pro"
          tagline="Optimize what you already own, with unlimited AI."
          plan={proPlan}
          billing={billing}
          badge={{ label: "Best value", cls: "bg-secondary text-secondary-foreground" }}
          portfolioLabel="Unlimited portfolios"
          perks={[
            <><b>Everything in Core</b>, plus:</>,
            <><b>Holdings Optimizer</b> — AI rebalancing of your real positions with a whole-share buy/sell list</>,
            <><b>Unlimited AI</b> and unlimited portfolios — no monthly caps</>,
            <><b>AI explanation chats saved</b> with every portfolio · branded PDF reports</>,
            <><b>Priority backtests</b> · locked-in yearly pricing</>,
          ]}
          buttonAccent="default"
          buttonTestId={TID.proSubscribe}
          onSubscribe={() => startCheckout(proPlan.lookupKey)}
          isCurrent={isPro && (sub?.lookup_key?.startsWith("pro") || !sub?.lookup_key)}
          isPro={isPro}
          nativeTier="pro"
          keepAfterTrial={!!sub?.pro_trial_ends_at}
          subSource={sub?.source}
          onNativePurchased={refresh}
          loading={loading}
          openBillingPortal={openBillingPortal}
          portalLoading={portalLoading}
        />

        {/* Card 4 — Team card moved to Admin Control Room (2026-02-13). */}
      </div>
      {isNative() && <NativeIapFooter onDone={refresh} />}

      {/* Trust strip */}
      <div className="mt-6 max-w-3xl mx-auto rounded-md border border-border bg-muted/30 px-4 py-3">
        <div className="flex items-center gap-2">
          <GitCompareArrows className="w-4 h-4 text-secondary" />
          <div className="mono text-[10px] uppercase tracking-widest text-muted-foreground">Which one is right for you?</div>
        </div>
        <p className="text-xs text-foreground/80 mt-1.5 leading-relaxed">
          <b>Free</b> shows you what a dividend portfolio could pay — backtests, Ask DPP and <b>4 AI actions a month</b>.
          <b> Core</b> connects your real brokerage and plans your income with <b>10 AI actions a month</b> across 10 portfolios.
          <b> Pro</b> optimizes the holdings you already own, with <b>unlimited AI</b>, saved AI chats and branded reports.
        </p>
      </div>

      {/* ------------------------------------------------------------
          QUICK-SCAN FEATURE CHART — killer differentiators only
      ------------------------------------------------------------ */}
      <section className="mt-14" id="pricing-feature-chart" data-testid={TID.featureChart}>
        <div className="text-center max-w-2xl mx-auto">
          <div className="mono text-[10px] uppercase tracking-[0.25em] text-secondary">Quick scan</div>
          <h2 className="serif text-3xl sm:text-4xl mt-2">Tier at a glance</h2>
          <p className="text-sm text-muted-foreground mt-2">The 10 things investors ask about most — everything else is in the detailed table below.</p>
        </div>
        {/* Desktop table — stays the way it was. Hidden on <sm. */}
        <Card className="mt-6 overflow-hidden hidden sm:block">
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-muted/40">
                  <tr>
                    <th className="text-left px-5 py-3 mono text-[10px] uppercase tracking-widest text-muted-foreground">Feature</th>
                    <th className="px-5 py-3 mono text-[10px] uppercase tracking-widest text-muted-foreground text-center">Free</th>
                    <th className="px-5 py-3 mono text-[10px] uppercase tracking-widest text-emerald-600 text-center">Core</th>
                    <th className="px-5 py-3 mono text-[10px] uppercase tracking-widest text-secondary text-center">Pro</th>
                  </tr>
                </thead>
                <tbody>
                  {FEATURE_CHART.map((row, i) => (
                    <tr key={row.name} className={i % 2 ? "bg-muted/20" : ""}>
                      <td className="px-5 py-3 text-foreground/90">{row.name}</td>
                      <td className="px-5 py-3"><div className="flex justify-center"><Marker v={row.free} /></div></td>
                      <td className="px-5 py-3"><div className="flex justify-center"><Marker v={row.beginner} accent="beginner" /></div></td>
                      <td className="px-5 py-3"><div className="flex justify-center"><Marker v={row.pro} accent="pro" /></div></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>

        {/* Mobile card layout — each feature becomes its own card with a
            2×2 grid of tier cells. Each cell prints its tier label above the
            value so mobile users don't have to remember which column is
            which. Rendered only under sm to keep desktop pristine. */}
        <div className="mt-6 space-y-3 sm:hidden" data-testid="pricing-feature-chart-mobile">
          {FEATURE_CHART.map((row) => (
            <Card key={row.name} className="overflow-hidden">
              <CardContent className="p-4">
                <div className="text-sm text-foreground/90 font-medium mb-3">{row.name}</div>
                <div className="grid grid-cols-3 gap-2">
                  {[
                    { label: "Free",     v: row.free,     accent: undefined,   cls: "text-muted-foreground" },
                    { label: "Core", v: row.beginner, accent: "beginner",  cls: "text-emerald-600" },
                    { label: "Pro",      v: row.pro,      accent: "pro",       cls: "text-secondary" },
                  ].map((c) => (
                    <div key={c.label} className="rounded-md border border-border bg-muted/20 px-3 py-2 flex flex-col items-center gap-1">
                      <span className={`mono text-[9px] uppercase tracking-widest ${c.cls}`}>{c.label}</span>
                      <Marker v={c.v} accent={c.accent} />
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
        <div className="text-center mt-4">
          <a
            href="#pricing-features-list"
            onClick={(e) => {
              e.preventDefault();
              document.getElementById("pricing-features-list")?.scrollIntoView({ behavior: "smooth", block: "start" });
            }}
            className="mono text-[11px] uppercase tracking-widest text-secondary hover:text-secondary/80 transition-colors"
            data-testid="pricing-see-included"
          >
            See the full comparison → 60+ features
          </a>
        </div>
      </section>

      {/* ------------------------------------------------------------
          DETAILED COMPARISON — the full feature-by-feature table
      ------------------------------------------------------------ */}
      <section id="pricing-features-list" data-testid={TID.featuresList} className="mt-16">
        <div className="text-center max-w-2xl mx-auto">
          <div className="mono text-[10px] uppercase tracking-[0.25em] text-secondary">Everything included</div>
          <h2 className="serif text-3xl sm:text-4xl mt-2">Detailed feature comparison</h2>
          <p className="text-sm text-muted-foreground mt-2">Every capability across the app, grouped by area.</p>
        </div>

        <div className="mt-8">
          {/* Column headers */}
          <div className="grid grid-cols-[1fr,72px,80px,72px] gap-2 pb-2 border-b border-border">
            <div className="mono text-[10px] uppercase tracking-widest text-muted-foreground">Feature</div>
            <div className="mono text-[10px] uppercase tracking-widest text-muted-foreground text-center">Free</div>
            <div className="mono text-[10px] uppercase tracking-widest text-emerald-600 text-center">Core</div>
            <div className="mono text-[10px] uppercase tracking-widest text-secondary text-center">Pro</div>
          </div>

          <div className="mt-2 space-y-6">
            {FEATURE_GROUPS.map((g) => (
              <div key={g.title} data-testid={`feature-group-${g.title.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
                <div className="flex items-center gap-2 mt-4 mb-2">
                  <div className="w-7 h-7 rounded-md bg-primary/10 text-primary grid place-items-center">
                    <g.icon className="w-3.5 h-3.5" />
                  </div>
                  <h3 className="serif text-lg">{g.title}</h3>
                </div>
                <ul className="divide-y divide-border rounded-md border border-border bg-card overflow-hidden">
                  {g.items.map((it) => (
                    <li
                      key={it.name}
                      className="grid grid-cols-[1fr,72px,80px,72px] gap-2 px-4 py-2.5 items-center text-sm"
                    >
                      <div className="text-foreground/85 flex items-center gap-2">
                        <span>{it.name}</span>
                        {it.badge && (
                          <span className="mono text-[9px] uppercase tracking-widest px-1.5 py-0.5 rounded bg-primary/10 text-primary border border-primary/20">
                            {it.badge}
                          </span>
                        )}
                      </div>
                      <div className="flex justify-center"><Marker v={it.free} /></div>
                      <div className="flex justify-center"><Marker v={it.beginner} accent="beginner" /></div>
                      <div className="flex justify-center"><Marker v={it.pro} accent="pro" /></div>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
            {/* Team-only comparison section removed 2026-02-13 — Team pricing
                is now an Admin Control Room concern. */}
          </div>

          <p className="text-[11px] text-muted-foreground italic mt-6 text-center">
            Educational tool — not investment advice. Data from Yahoo Finance via yfinance.
          </p>
        </div>
      </section>

      {/* Competitor scorecard */}
      <PricingScorecard />

      {/* Referral block — full-width below */}
      {user && referral && (
        <div className="mt-14">
          <Card className="overflow-hidden">
            <CardContent className="p-8">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8 items-center">
                <div>
                  <div className="inline-flex items-center gap-2 mono text-[10px] uppercase tracking-[0.25em] text-secondary">
                    <Gift className="w-3.5 h-3.5" /> Refer &amp; earn
                  </div>
                  <h3 className="serif text-3xl mt-3">Give a friend DPP — get a free month.</h3>
                  <p className="text-sm text-muted-foreground mt-2">
                    Every friend you refer earns <span className="font-semibold text-foreground">you 30 days of Pro</span> the moment they upgrade. Their card is charged normally; yours isn&apos;t.
                  </p>
                </div>
                <div>
                  <div className="flex gap-2">
                    <Input
                      readOnly
                      value={shareUrl}
                      data-testid={TID.referralCode}
                      className="mono text-xs bg-muted"
                      onFocus={(e) => e.target.select()}
                    />
                    <Button
                      variant="secondary"
                      onClick={copyLink}
                      data-testid={TID.referralCopy}
                      className="shrink-0 rounded-full"
                    >
                      <Copy className="w-4 h-4 mr-1" /> Copy
                    </Button>
                  </div>
                  <div className="grid grid-cols-2 gap-4 mt-4">
                    <Stat label="Friends referred" value={referral.referred_count} testId={TID.referralCount} />
                    <Stat label="Months earned" value={referral.months_earned} accent />
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      <div className="mt-14 text-center text-xs text-muted-foreground">
        Web payments processed by Stripe with tax handled automatically; in-app subscriptions are billed by the App Store or Google Play.
        <button onClick={refresh} className="ml-2 underline underline-offset-2 hover:text-primary">Refresh status</button>
      </div>

      <PlanPickerQuiz open={quizOpen} onOpenChange={setQuizOpen} onPick={highlightPlan} />
      <ContactDialog
        open={contactOpen}
        onOpenChange={setContactOpen}
        defaultEmail={user?.email || ""}
        defaultName={user?.name || ""}
      />
    </div>
  );
}

function Stat({ label, value, accent, testId }) {
  return (
    <div className="rounded-md border border-border bg-card px-3 py-2" data-testid={testId}>
      <div className="mono text-[10px] uppercase tracking-widest text-muted-foreground">{label}</div>
      <div className={`serif text-2xl ${accent ? "text-secondary" : "text-foreground"}`}>{value}</div>
    </div>
  );
}

// ---------- Small helpers ----------

function PerkRow({ children }) {
  return (
    <li className="flex items-start gap-2">
      <Check className="w-4 h-4 text-primary mt-0.5 shrink-0" />
      <span className="text-foreground/85">{children}</span>
    </li>
  );
}

// Reusable pricing card used for Core + Pro. Keeps the two cards
// visually identical while letting them differ on plan, badge, and copy.
function PricingTierCard({
  testId, icon: Icon, iconClass, borderClass, name, tagline, plan, billing,
  badge, portfolioLabel, perks, buttonAccent, buttonTestId, onSubscribe,
  isCurrent, isPro, loading, openBillingPortal, portalLoading, trialCta,
  nativeTier, subSource, onNativePurchased, keepAfterTrial,
}) {
  return (
    <Card className={`relative h-full ${borderClass} card-hover overflow-hidden`} data-testid={testId}>
      {badge && (
        <div className="absolute top-3 right-3">
          <span className={`mono text-[9px] uppercase tracking-widest px-2 py-1 rounded-full inline-flex items-center gap-1 ${badge.cls}`}>
            <Sparkles className="w-3 h-3" /> {badge.label}
          </span>
        </div>
      )}
      <CardContent className="p-6 flex flex-col h-full">
        <div className="flex items-center gap-2">
          <Icon className={`w-5 h-5 ${iconClass}`} />
          <h2 className="serif text-2xl">{name}</h2>
        </div>
        <p className="text-xs text-muted-foreground mt-1.5">{tagline}</p>

        <div className="mt-4 flex items-baseline gap-1.5 flex-wrap">
          <span className="serif text-4xl">${plan.amount.toFixed(2)}</span>
          <span className="mono text-xs text-muted-foreground">{plan.period}</span>
        </div>
        {plan.perMonthEquivalent && (
          <div className="mono text-[10px] text-muted-foreground mt-1">{plan.perMonthEquivalent}</div>
        )}
        {plan.savings && (
          <span className="mono text-[9px] uppercase tracking-widest px-2 py-0.5 mt-1 self-start rounded-full bg-secondary/15 text-secondary border border-secondary/30">
            {plan.savings}
          </span>
        )}
        <div className="mono text-[10px] uppercase tracking-widest text-muted-foreground mt-2">{portfolioLabel}</div>

        {/* 7-day free-trial ribbon (Core only). Sits inline with the price
            so it feels like part of the offer, not a footnote. */}
        {trialCta?.show && (
          <div className="mt-3 inline-flex items-center gap-1.5 self-start rounded-full px-2.5 py-1 bg-emerald-500/10 text-emerald-700 dark:text-emerald-400 border border-emerald-500/30">
            <Gift className="w-3 h-3" />
            <span className="mono text-[10px] uppercase tracking-widest">7-day free trial · no card</span>
          </div>
        )}

        <ul className="mt-4 space-y-1.5 text-sm flex-1">
          {perks.map((p, i) => <PerkRow key={i}>{p}</PerkRow>)}
        </ul>

        {/* Trial CTA sits ABOVE the paid subscribe button — friction-free
            entry that the user is more likely to click first. */}
        {trialCta?.show && (
          <Button
            data-testid={trialCta.testId}
            disabled={trialCta.loading}
            onClick={trialCta.onClick}
            variant="outline"
            className="w-full mt-5 rounded-full border-emerald-500/40 text-emerald-700 dark:text-emerald-400 hover:bg-emerald-500/10"
          >
            {trialCta.loading
              ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Activating…</>
              : <><Gift className="w-4 h-4 mr-2" />Start 7-day free trial · no card</>}
          </Button>
        )}

        {/* App Store / Play rules: digital subscriptions in the native app go through store billing (RevenueCat). */}
        {isNative() ? (
          <NativeIapButton
            tier={nativeTier}
            billing={billing}
            isCurrent={isCurrent}
            source={subSource}
            onDone={onNativePurchased}
            variant={buttonAccent === "secondary" ? "secondary" : "default"}
            className={`w-full ${trialCta?.show ? "mt-2" : "mt-5"} rounded-full`}
            testId={buttonTestId}
          />
        ) : (<>
        <Button
          data-testid={buttonTestId}
          disabled={loading || isCurrent}
          onClick={onSubscribe}
          variant={buttonAccent === "secondary" ? "secondary" : "default"}
          className={`w-full ${trialCta?.show ? "mt-2" : "mt-5"} rounded-full`}
        >
          {isCurrent
            ? "You're subscribed"
            : loading
            ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Redirecting…</>
            : keepAfterTrial
            ? `Keep Pro after your trial · $${plan.amount.toFixed(2)}/${billing === "yearly" ? "yr" : "mo"}`
            : billing === "yearly"
            ? `Start 3-day trial · $${plan.amount.toFixed(2)}/yr`
            : `Subscribe · $${plan.amount.toFixed(2)}/mo`}
        </Button>
        {isCurrent && (
          <Button
            variant="outline"
            disabled={portalLoading}
            onClick={openBillingPortal}
            className="w-full mt-2 rounded-full"
          >
            {portalLoading ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Opening…</> : "Manage billing"}
          </Button>
        )}
        </>)}
      </CardContent>
    </Card>
  );
}

// ---------- Team / Enterprise card ----------

export function TeamCard({ user }) {
  const [seats, setSeats] = React.useState(TEAM.minSeats);
  const [open, setOpen] = React.useState(false);
  const [submitting, setSubmitting] = React.useState(false);
  const [email, setEmail] = React.useState(user?.email || "");
  const [company, setCompany] = React.useState("");
  const [notes, setNotes] = React.useState("");
  const monthlyEstimate = (Number(seats) || 0) * TEAM.perSeatMonthly;
  const yearlyEstimate = monthlyEstimate * 12 * 0.8; // matches paid tier's 20% yearly discount

  const submit = async () => {
    if (!email || !email.includes("@")) { toast.error("Enter a valid work email"); return; }
    setSubmitting(true);
    try {
      // Reuse waitlist API — feature payload doubles as the sales-lead record.
      await api.post("/waitlist", {
        email,
        feature: `Team tier — ${seats} seats · ${company || "no company"} · ${notes || ""}`.slice(0, 80),
      });
      toast.success("Got it — we'll email you within 1 business day");
      setOpen(false);
      setCompany(""); setNotes("");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not submit — please email hello@dividendportfolioplanner.com");
    } finally { setSubmitting(false); }
  };

  return (
    <>
      <Card className="h-full border border-primary/40 bg-primary/[0.02] card-hover" data-testid="pricing-team-card">
        <CardContent className="p-6 flex flex-col h-full">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Building2 className="w-5 h-5 text-primary" />
              <h2 className="serif text-2xl">Team</h2>
            </div>
            <span className="mono text-[9px] uppercase tracking-widest px-2 py-0.5 rounded-full bg-primary/10 text-primary border border-primary/20">
              Enterprise
            </span>
          </div>
          <p className="text-xs text-muted-foreground mt-1.5">
            Onboard your whole desk. Core capabilities, per seat.
          </p>

          <div className="mt-4 flex items-baseline gap-1.5 flex-wrap">
            <span className="serif text-4xl">${TEAM.perSeatMonthly.toFixed(2)}</span>
            <span className="mono text-xs text-muted-foreground">/ seat · month</span>
          </div>
          <div className="mono text-[10px] uppercase tracking-widest text-muted-foreground mt-2">
            Min {TEAM.minSeats} seats · Core capabilities per seat
          </div>

          {/* Compact seat estimator */}
          <div className="mt-3 rounded-md border border-border bg-card px-3 py-2">
            <div className="flex items-center justify-between gap-2">
              <label htmlFor="seats" className="mono text-[10px] uppercase tracking-widest text-muted-foreground">Seats</label>
              <div className="flex items-center gap-1.5">
                <button
                  type="button"
                  onClick={() => setSeats((s) => Math.max(TEAM.minSeats, Number(s) - 1))}
                  className="w-6 h-6 rounded-full border border-border grid place-items-center hover:bg-muted text-xs"
                  aria-label="Fewer seats"
                >−</button>
                <input
                  id="seats"
                  type="number"
                  min={TEAM.minSeats}
                  max={5000}
                  value={seats}
                  onChange={(e) => setSeats(Math.max(TEAM.minSeats, Number(e.target.value) || TEAM.minSeats))}
                  className="w-12 text-center mono text-sm bg-transparent focus:outline-none"
                  data-testid={TID.teamSeats}
                />
                <button
                  type="button"
                  onClick={() => setSeats((s) => Number(s) + 1)}
                  className="w-6 h-6 rounded-full border border-border grid place-items-center hover:bg-muted text-xs"
                  aria-label="More seats"
                >+</button>
              </div>
            </div>
            <div className="mono text-[10px] text-muted-foreground mt-1">
              <span className="text-primary">${monthlyEstimate.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>/mo · <span className="text-secondary">${yearlyEstimate.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>/yr
            </div>
          </div>

          <ul className="mt-4 space-y-1.5 text-sm flex-1">
            {TEAM.perksSummary.map((p) => <PerkRow key={p}>{p}</PerkRow>)}
          </ul>

          <Button
            data-testid={TID.teamContact}
            onClick={() => setOpen(true)}
            className="w-full mt-5 rounded-full"
            variant="outline"
          >
            <Mail className="w-4 h-4 mr-2" /> Contact sales
          </Button>
        </CardContent>
      </Card>

      {/* Contact-sales dialog */}
      <TeamContactDialog
        open={open}
        onOpenChange={setOpen}
        email={email}
        setEmail={setEmail}
        company={company}
        setCompany={setCompany}
        seats={seats}
        setSeats={setSeats}
        notes={notes}
        setNotes={setNotes}
        onSubmit={submit}
        submitting={submitting}
        monthlyEstimate={monthlyEstimate}
      />
    </>
  );
}

function TeamContactDialog({ open, onOpenChange, email, setEmail, company, setCompany, seats, setSeats, notes, setNotes, onSubmit, submitting, monthlyEstimate }) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="team-contact-dialog">
        <DialogHeader>
          <DialogTitle className="serif text-2xl">Talk to sales</DialogTitle>
          <DialogDescription>
            Give us a few details — we&apos;ll email a proposal + invoice link within 1 business day and set up your admin pre-approvals so seats activate on first Google sign-in.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label htmlFor="tc-email">Work email</Label>
            <Input id="tc-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@company.com" data-testid="team-contact-email" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label htmlFor="tc-company">Company</Label>
              <Input id="tc-company" value={company} onChange={(e) => setCompany(e.target.value)} placeholder="Acme Corp" data-testid="team-contact-company" />
            </div>
            <div>
              <Label htmlFor="tc-seats">Seats</Label>
              <Input
                id="tc-seats" type="number" min={TEAM.minSeats} max={5000}
                value={seats}
                onChange={(e) => setSeats(Math.max(TEAM.minSeats, Number(e.target.value) || TEAM.minSeats))}
                data-testid="team-contact-seats"
              />
            </div>
          </div>
          <div>
            <Label htmlFor="tc-notes">Notes (optional)</Label>
            <Textarea id="tc-notes" rows={3} value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Timeline, use-case, procurement notes…" data-testid="team-contact-notes" />
          </div>
          <div className="rounded-md border border-border bg-muted/30 px-3 py-2 mono text-xs text-muted-foreground">
            Estimate: <span className="text-foreground">${monthlyEstimate.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}/mo</span> · billing set up manually after intro call
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={onSubmit} disabled={submitting} className="rounded-full" data-testid="team-contact-submit">
            {submitting ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Sending…</> : "Send request"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

